import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { APIService } from '../APIService/api.service';
import { AuthService } from '../Auth/auth.service';
import { PrüfungsTypStore } from '../Shared/prüfung-typ.store';

@Injectable({
  providedIn: 'root',
})
export class BasicStoreService {
  private apiservice = inject(APIService);
  private authservice = inject(AuthService);
  private pruefungsTyp = inject(PrüfungsTypStore);

  asyncStore: { [key: string]: BehaviorSubject<any> } = {
    anlagen: new BehaviorSubject(undefined),
    maengel: new BehaviorSubject(undefined),
    einstellung: new BehaviorSubject(undefined),
    dashboard: new BehaviorSubject(undefined),
  };

  detailViewStore: Record<string, any> = {};
  loggedStatus: boolean;

  constructor() {
    this.authservice
      .getLoggedIn()
      .pipe(first(data0 => data0 === true))
      .subscribe(async _ => {
        this.apiservice.refreshJWT();
        await this.initialize();
      });
  }

  private async initialize() {
    try {
      firstValueFrom(this.apiservice.getAnlagen(this.pruefungsTyp.KPRUVID())).then(anlagen =>
        this.setComponentStore('anlagen', { anlagen })
      );

      this.setDashboardStore();

      firstValueFrom(this.apiservice.getMaengel(false, this.pruefungsTyp.KPRUVID())).then(maengel =>
        this.setComponentStore('maengel', { maengel })
      );

      await this.initSettings();
    } catch (error) {
      console.error('Initialization error:', error);
    }
  }
  public async setDashboardStore() {
    firstValueFrom(this.apiservice.getStatistik({ typ: 'dashboard' })).then(dashboardData => {
      this.setComponentStore('dashboard', { dashboard: dashboardData });
    });
  }

  private async initSettings() {
    try {
      const data: any = await firstValueFrom(this.apiservice.getINIOptions());
      if (data) {
        this.setComponentStore('einstellung', { einstellung: data.rows[0] });
      }
    } catch (error) {
      console.error('Settings initialization error:', error);
    }
  }

  public getComponentStoreAsync(name: string, parseValidJson = false) {
    if (parseValidJson)
      return this.asyncStore[name]?.pipe(
        map(data => data && JSON.parse(data)),
        filter(data => !!data)
      );
    return this.asyncStore[name] || undefined;
  }

  public getComponentStore(componentName: string) {
    let val = this.asyncStore[componentName]?.getValue();
    if (typeof val === 'string') val = JSON.parse(val);
    return val || undefined;
  }

  public setComponentStore(componentName: string, componentConfig: any, parseJSON: boolean = true) {
    if (!this.asyncStore[componentName]) {
      this.asyncStore[componentName] = new BehaviorSubject(undefined);
    }
    if (componentConfig) {
      const value = parseJSON ? JSON.stringify(componentConfig) : componentConfig;
      this.asyncStore[componentName].next(value);
    }
  }

  public resetComponentStore(componentName: string, componentConfig: any, parseJSON: boolean = true) {
    const before = this.getComponentStore(componentName);
    this.setComponentStore(componentName, { ...before, ...componentConfig }, parseJSON);
  }

  public deleteComponentStoreAsync(componentName: string) {
    if (this.asyncStore[componentName]) {
      this.asyncStore[componentName].next(undefined);
    }
  }

  public getDetailViewStore() {
    return this.detailViewStore;
  }

  public setDetailViewStore(dvconf: Record<string, any>) {
    this.detailViewStore = dvconf;
  }

  public async reloadTabs({
    emitter: reloadTabsIsReady = undefined,
    reloadAnlagen = true,
  }: { emitter?: Subject<any>; reloadAnlagen?: boolean } = {}) {
    if (reloadAnlagen) {
      const anlagen = await firstValueFrom(this.apiservice.getAnlagen(this.pruefungsTyp.KPRUVID()));
      this.resetComponentStore('anlagen', { anlagen });
      reloadTabsIsReady?.next({ refreshAnlagen: true });
    }

    if (this.apiservice.isRep) return;

    const maengel = await firstValueFrom(this.apiservice.getMaengel(false, this.pruefungsTyp.KPRUVID()));
    this.resetComponentStore('maengel', { maengel });
    reloadTabsIsReady?.next({ refreshMaengel: true });

    this.resetComponentStore('auftraege', { auftraege: null });
  }
}
